import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Calendar, MapPin, Plus, X, Loader2, Trash2, Pencil, Bell, BellOff } from "lucide-react";
import { toast } from "sonner";
import { tr } from "@/lib/translate";

export default function AgendaPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  // "Mon agenda" = only events this user has checked/subscribed to
  // (POST /events/{id}/interest) — distinct from every artist's every
  // event, which is what this page shows by default.
  const [mineOnly, setMineOnly] = useState(false);
  const [interested, setInterested] = useState({}); // event_id -> bool

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/events", { params: mineOnly ? { mine: true } : {} });
      setEvents(data);
      if (user) {
        const entries = await Promise.all(data.map(async (e) => {
          try { const { data: d } = await api.get(`/events/${e.id}/interest`); return [e.id, d.interested]; }
          catch { return [e.id, false]; }
        }));
        setInterested(Object.fromEntries(entries));
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [mineOnly]);

  const remove = async (id) => {
    try { await api.delete(`/events/${id}`); toast.success(tr("Deleted")); load(); }
    catch { toast.error(tr("Could not delete")); }
  };

  const toggleInterest = async (eventId) => {
    if (!user) { toast.error(tr("Sign in to interact")); return; }
    try {
      const { data } = await api.post(`/events/${eventId}/interest`);
      setInterested((s) => ({ ...s, [eventId]: data.interested }));
      if (mineOnly && !data.interested) load(); // dropped from "mon agenda" — refresh the filtered list
    } catch (e) { toast.error(tr(e.response?.data?.detail || "Could not save")); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6">
      <header className="anim-in mb-6 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#8A2BE2] font-bold">{t.agenda.title}</p>
          <h1 className="text-3xl sm:text-4xl font-black font-display text-white">{t.agenda.title}</h1>
          <p className="text-zinc-400 mt-1 text-sm max-w-xl">{t.agenda.sub}</p>
        </div>
        {user?.role === "artist" && (
          <button onClick={() => { setEditingEvent(null); setShowForm(true); }} className="btn-primary inline-flex items-center gap-2" data-testid="add-event-btn">
            <Plus className="w-4 h-4" /> {t.agenda.add}
          </button>
        )}
      </header>

      {user && (
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setMineOnly(false)}
            className={`px-4 py-1.5 rounded-full text-xs font-bold transition ${!mineOnly ? "bg-white text-black" : "bg-white/5 text-zinc-400 border border-white/10"}`}
            data-testid="agenda-filter-all"
          >
            Tous les événements
          </button>
          <button
            onClick={() => setMineOnly(true)}
            className={`px-4 py-1.5 rounded-full text-xs font-bold transition ${mineOnly ? "bg-white text-black" : "bg-white/5 text-zinc-400 border border-white/10"}`}
            data-testid="agenda-filter-mine"
          >
            Mon agenda
          </button>
        </div>
      )}

      {renderAgendaList(loading, events, t, user, remove, interested, toggleInterest, (e) => { setEditingEvent(e); setShowForm(true); })}

      {showForm && (
        <EventForm
          existing={editingEvent}
          onClose={() => { setShowForm(false); setEditingEvent(null); }}
          onSaved={() => { setShowForm(false); setEditingEvent(null); load(); }}
        />
      )}
    </div>
  );
}

function renderAgendaList(loading, events, t, user, remove, interested, toggleInterest, edit) {
  if (loading) return <p className="text-center text-zinc-500 py-12">{t.common.loading}</p>;
  if (events.length === 0) return <p className="text-center text-zinc-500 py-12">{t.agenda.empty}</p>;
  return (
    <div className="space-y-3" data-testid="agenda-list">
      {events.map((e) => {
        const d = new Date(e.date);
        const isOwner = user?.id === e.artist_id;
        return (
          <div key={e.id} className="card-flat p-4 flex items-center gap-4 anim-in">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#FF0050] to-[#8A2BE2] grid place-items-center text-white shrink-0">
              <div className="text-center">
                <div className="text-[10px] uppercase tracking-wider opacity-80">{d.toLocaleString(undefined, { month: "short" })}</div>
                <div className="text-xl font-black leading-none">{d.getDate()}</div>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold truncate">{e.title}</p>
              <p className="text-xs text-zinc-400 flex items-center gap-2"><MapPin className="w-3 h-3" />{e.location} · {d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
              <p className="text-xs text-zinc-500 mt-1">by <span className="text-zinc-300">{e.artist_name}</span></p>
            </div>
            <span className="chip bg-white/5 text-zinc-300 border border-white/5 capitalize">{t.agenda.types[e.type] || e.type}</span>
            {!isOwner && user && (
              <button
                onClick={() => toggleInterest(e.id)}
                title={interested[e.id] ? "Retirer de mon agenda" : "Ajouter à mon agenda"}
                className={`p-2 rounded-full transition shrink-0 ${interested[e.id] ? "bg-[#00F2FE]/15 text-[#00F2FE]" : "text-zinc-500 hover:text-white"}`}
                data-testid={`event-interest-${e.id}`}
              >
                {interested[e.id] ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
              </button>
            )}
            {isOwner && (
              <>
                <button onClick={() => edit(e)} className="p-2 text-zinc-500 hover:text-white transition" data-testid={`edit-event-${e.id}`} aria-label="edit">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => remove(e.id)} className="p-2 text-zinc-500 hover:text-red-400 transition" data-testid={`del-event-${e.id}`} aria-label="delete">
                  <Trash2 className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

function EventForm({ existing, onClose, onSaved }) {
  const { t } = useI18n();
  const isEdit = !!existing;
  const [d, setD] = useState(
    existing
      ? { title: existing.title, description: existing.description || "", location: existing.location,
          date: toLocalDatetimeInput(existing.date), type: existing.type }
      : { title: "", description: "", location: "", date: "", type: "concert" }
  );
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const iso = new Date(d.date).toISOString();
      if (isEdit) {
        await api.patch(`/events/${existing.id}`, { ...d, date: iso });
        toast.success(tr("Event updated"));
      } else {
        await api.post("/events", { ...d, date: iso });
        toast.success(tr("Event created"));
      }
      onSaved();
    } catch { toast.error(tr(isEdit ? "Could not update" : "Could not create")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-md grid place-items-center p-4" data-testid="event-modal">
      <div className="w-full max-w-md bg-zinc-950 border border-white/10 rounded-3xl p-6 anim-in relative">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full bg-white/5 hover:bg-white/10" aria-label="close"><X className="w-4 h-4 text-zinc-400" /></button>
        <h3 className="text-2xl font-black font-display text-white mb-4">{isEdit ? (t.agenda.edit ?? "Modifier l'événement") : t.agenda.add}</h3>
        <form onSubmit={submit} className="space-y-3">
          <input required className="input-base" value={d.title} onChange={(e) => setD({ ...d, title: e.target.value })} placeholder={t.agenda.name} data-testid="event-name" />
          <textarea rows={2} className="input-base" value={d.description} onChange={(e) => setD({ ...d, description: e.target.value })} placeholder={t.agenda.desc} data-testid="event-desc" />
          <input required className="input-base" value={d.location} onChange={(e) => setD({ ...d, location: e.target.value })} placeholder={t.agenda.location} data-testid="event-location" />
          <input required type="datetime-local" className="input-base" value={d.date} onChange={(e) => setD({ ...d, date: e.target.value })} data-testid="event-date" />
          <select className="input-base" value={d.type} onChange={(e) => setD({ ...d, type: e.target.value })} data-testid="event-type">
            <option value="concert">{t.agenda.types.concert}</option>
            <option value="signing">{t.agenda.types.signing}</option>
            <option value="meetup">{t.agenda.types.meetup}</option>
            <option value="other">{t.agenda.types.other}</option>
          </select>
          <button type="submit" disabled={busy} className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="event-create-btn">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}{isEdit ? (t.agenda.save ?? t.profile.save) : t.agenda.create}
          </button>
        </form>
      </div>
    </div>
  );
}

// `datetime-local` inputs need "YYYY-MM-DDTHH:mm" in LOCAL time, but the API
// returns a UTC ISO string — converting naively (slicing the ISO string)
// would show the wrong local hour, so build it from the Date object's own
// local getters instead.
function toLocalDatetimeInput(isoString) {
  const d = new Date(isoString);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
